import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { UseFormReturn } from "react-hook-form";
import type { ClientAddSchema } from "../../hooks/useClientForm";
import type {
  ClientOverviewResponse,
  ClientOverviewWithSubscriptionsResponse,
} from "../../types/client.types";

interface ClientAddFormProps {
  form: UseFormReturn<ClientAddSchema>;
  showPassword: boolean;
  onTogglePassword: () => void;
  isEdit?: boolean;
  clientData?: ClientOverviewResponse | ClientOverviewWithSubscriptionsResponse;
}

interface TextFieldProps {
  form: UseFormReturn<ClientAddSchema>;
  name: keyof ClientAddSchema;
  label: string;
  placeholder: string;
  type?: string;
  autoComplete?: string;
  disabled?: boolean;
}

function TextField({
  form,
  name,
  label,
  placeholder,
  type = "text",
  autoComplete,
  disabled,
}: TextFieldProps) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-1">
          <FormLabel className="text-black-500 text-xs leading-3 font-medium tracking-normal uppercase">
            {label}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              type={type}
              placeholder={placeholder}
              className="placeholder:text-black-300 bg-admin-input h-[45px] w-full text-white focus:ring-1 active:ring-1"
              autoComplete={autoComplete}
              variant="filled"
              disabled={disabled}
            />
          </FormControl>
          <FormMessage className="text-xs text-red-600" />
        </FormItem>
      )}
    />
  );
}

export function ClientAddForm({
  form,
  showPassword,
  onTogglePassword,
  isEdit = false,
}: ClientAddFormProps) {
  return (
    <Form {...form}>
      <form className="space-y-5 p-0.5">
        <div className="grid grid-cols-2 gap-3">
          <TextField
            form={form}
            name="first_name"
            label="First Name"
            placeholder="John"
            autoComplete="name"
          />
          <TextField
            form={form}
            name="last_name"
            label="Last Name"
            placeholder="Doe"
            autoComplete="name"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TextField
            form={form}
            name="username"
            label="Username"
            placeholder="john_doe"
            autoComplete="username"
          />
          <TextField
            form={form}
            name="email"
            label="Email"
            placeholder="john.doe@example.com"
            type="email"
            autoComplete="email"
          />
        </div>

        {!isEdit && (
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="mt-3 space-y-1">
                <FormLabel className="text-black-500 text-xs leading-3 font-medium tracking-normal uppercase">
                  Temporary Password
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••"
                      className="placeholder:text-black-300 bg-admin-input h-[45px] w-full text-white focus:ring-1 active:ring-1"
                      autoComplete="current-password"
                      variant="filled"
                      disabled
                      value={form.watch("password")}
                    />
                  </FormControl>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-1/2 right-3 h-6 w-6 -translate-y-1/2 text-white"
                    onClick={onTogglePassword}
                  >
                    {showPassword ? (
                      <i className="ri-eye-off-line" />
                    ) : (
                      <i className="ri-eye-line" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Hide password" : "Show password"}
                    </span>
                  </Button>
                </div>
                <FormMessage className="text-xs !text-red-600" />
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
}
